import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/client';
import {
  FiArrowLeft,
  FiHome,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCreditCard,
  FiSearch,
  FiDollarSign,
} from 'react-icons/fi';
import NotificationBell from '../../component/NotificationBell/NotificationBell';
import { luxisttConfirm } from '../../utils/luxisttSwal';
import './Dashboard.css';

const EXPENSE_CATEGORIES = [
  { value: 'utilities', label: 'Utilities' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'other', label: 'Other' },
];

const PropertyDetail = () => {
  const { propertyId } = useParams();
  const { canManage, hasPermission } = useAuth();
  const canManageExpenses = hasPermission('finance-expenses-manage');
  const [property, setProperty] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [cities, setCities] = useState([]);
  const [apartmentTypes, setApartmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPropertyModal, setEditPropertyModal] = useState(false);
  const [addApartmentModal, setAddApartmentModal] = useState(false);
  const [editApartmentModal, setEditApartmentModal] = useState(null);
  const [addExpenseModal, setAddExpenseModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [propertyForm, setPropertyForm] = useState({ name: '', city_id: '', address_street: '', address_postcode: '' });
  const [apartmentForm, setApartmentForm] = useState({
    name: '', capacity_adults: 2, capacity_children: 0, bathrooms: 1, apartment_type_id: '', size: '',
  });
  const [expenseForm, setExpenseForm] = useState({
    amount: '', category: 'utilities', expense_date: new Date().toISOString().slice(0, 10), description: '', apartment_id: '',
  });
  const [editingExpense, setEditingExpense] = useState(null);
  const [apartmentSearch, setApartmentSearch] = useState('');
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('');
  const [expenseDateFrom, setExpenseDateFrom] = useState('');
  const [expenseDateTo, setExpenseDateTo] = useState('');
  const [pricingApartment, setPricingApartment] = useState(null);
  const [apartmentPrices, setApartmentPrices] = useState([]);
  const [priceForm, setPriceForm] = useState({ start_date: '', end_date: '', price: '' });
  const [editingPriceId, setEditingPriceId] = useState(null);

  const load = async () => {
    if (!propertyId) return;
    try {
      const [propRes, aptRes, expRes, citiesRes, typesRes] = await Promise.all([
        api.get(`/owner/properties/${propertyId}`),
        api.get(`/owner/properties/${propertyId}/apartments`),
        api.get(`/owner/properties/${propertyId}/expenses`),
        api.get('/cities').catch(() => ({ data: [] })),
        api.get('/apartment-types').catch(() => ({ data: [] })),
      ]);
      setProperty(propRes.data);
      setPropertyForm({
        name: propRes.data.name || '',
        city_id: String(propRes.data.city_id || ''),
        address_street: propRes.data.address_street || '',
        address_postcode: propRes.data.address_postcode || '',
      });
      setApartments(aptRes.data || []);
      setExpenses(expRes.data || []);
      setCities(citiesRes.data || []);
      setApartmentTypes(typesRes.data || []);
    } catch (err) {
      console.error(err);
      setProperty(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const handleUpdateProperty = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.put(`/owner/properties/${propertyId}`, {
        name: propertyForm.name,
        city_id: Number(propertyForm.city_id),
        address_street: propertyForm.address_street,
        address_postcode: propertyForm.address_postcode || null,
      });
      setProperty((p) => (p ? { ...p, ...propertyForm } : null));
      setEditPropertyModal(false);
    } catch (err) {
      setError(err?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleAddApartment = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: apartmentForm.name,
        capacity_adults: Number(apartmentForm.capacity_adults),
        capacity_children: Number(apartmentForm.capacity_children),
        bathrooms: Number(apartmentForm.bathrooms),
        apartment_type_id: apartmentForm.apartment_type_id ? Number(apartmentForm.apartment_type_id) : null,
        size: apartmentForm.size ? Number(apartmentForm.size) : null,
      };
      const res = await api.post(`/owner/properties/${propertyId}/apartments`, payload);
      setApartments((prev) => [...prev, res.data]);
      setAddApartmentModal(false);
      setApartmentForm({ name: '', capacity_adults: 2, capacity_children: 0, bathrooms: 1, apartment_type_id: '', size: '' });
    } catch (err) {
      setError(err?.message || 'Failed to add apartment');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateApartment = async (e) => {
    e.preventDefault();
    if (!editApartmentModal) return;
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: apartmentForm.name,
        capacity_adults: Number(apartmentForm.capacity_adults),
        capacity_children: Number(apartmentForm.capacity_children),
        bathrooms: Number(apartmentForm.bathrooms),
        apartment_type_id: apartmentForm.apartment_type_id ? Number(apartmentForm.apartment_type_id) : null,
        size: apartmentForm.size ? Number(apartmentForm.size) : null,
      };
      const res = await api.put(`/owner/apartments/${editApartmentModal.id}`, payload);
      setApartments((prev) => prev.map((a) => (a.id === res.data.id ? res.data : a)));
      setEditApartmentModal(null);
    } catch (err) {
      setError(err?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApartment = async (apartment) => {
    const ok = await luxisttConfirm({
      title: 'Delete apartment?',
      text: `Permanently delete “${apartment.name}”? This cannot be undone.`,
      icon: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/owner/apartments/${apartment.id}`);
      setApartments((prev) => prev.filter((a) => a.id !== apartment.id));
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = {
        amount: Number(expenseForm.amount),
        category: expenseForm.category,
        expense_date: expenseForm.expense_date,
        description: expenseForm.description || null,
        apartment_id: expenseForm.apartment_id ? Number(expenseForm.apartment_id) : null,
      };
      const res = await api.post(`/owner/properties/${propertyId}/expenses`, payload);
      setExpenses((prev) => [res.data, ...prev]);
      setAddExpenseModal(false);
      setExpenseForm({
        amount: '', category: 'utilities', expense_date: new Date().toISOString().slice(0, 10), description: '', apartment_id: '',
      });
    } catch (err) {
      setError(err?.message || 'Failed to add expense');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateExpense = async (e) => {
    e.preventDefault();
    if (!editingExpense) return;
    setError('');
    setSaving(true);
    try {
      const payload = {
        amount: Number(expenseForm.amount),
        category: expenseForm.category,
        expense_date: expenseForm.expense_date,
        description: expenseForm.description || null,
        apartment_id: expenseForm.apartment_id ? Number(expenseForm.apartment_id) : null,
      };
      const res = await api.put(`/owner/expenses/${editingExpense.id}`, payload);
      setExpenses((prev) => prev.map((ex) => (ex.id === res.data.id ? res.data : ex)));
      setEditingExpense(null);
    } catch (err) {
      setError(err?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (expense) => {
    const ok = await luxisttConfirm({
      title: 'Delete expense?',
      text: 'Remove this expense from your records?',
      icon: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/owner/expenses/${expense.id}`);
      setExpenses((prev) => prev.filter((ex) => ex.id !== expense.id));
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    }
  };

  const openEditApartment = (apt) => {
    setEditApartmentModal(apt);
    setApartmentForm({
      name: apt.name,
      capacity_adults: apt.capacity_adults ?? 2,
      capacity_children: apt.capacity_children ?? 0,
      bathrooms: apt.bathrooms ?? 1,
      apartment_type_id: apt.apartment_type_id ? String(apt.apartment_type_id) : '',
      size: apt.size ?? '',
    });
    setError('');
  };

  const openEditExpense = (ex) => {
    setEditingExpense(ex);
    setExpenseForm({
      amount: String(ex.amount),
      category: ex.category || 'utilities',
      expense_date: (ex.expense_date || '').slice(0, 10),
      description: ex.description || '',
      apartment_id: ex.apartment_id ? String(ex.apartment_id) : '',
    });
    setError('');
  };

  const openPricing = (apt) => {
    setPricingApartment(apt);
    setApartmentPrices([]);
    setPriceForm({ start_date: '', end_date: '', price: '' });
    setEditingPriceId(null);
    setError('');
    api.get(`/owner/apartments/${apt.id}/prices`)
      .then((r) => setApartmentPrices(r.data || []))
      .catch(() => setApartmentPrices([]));
  };

  const handleAddPrice = async (e) => {
    e.preventDefault();
    if (!pricingApartment) return;
    setError('');
    setSaving(true);
    try {
      const res = await api.post(`/owner/apartments/${pricingApartment.id}/prices`, {
        start_date: priceForm.start_date,
        end_date: priceForm.end_date,
        price: Number(priceForm.price),
      });
      setApartmentPrices((prev) => [...prev, res.data].sort((a, b) => (a.start_date || '').localeCompare(b.start_date || '')));
      setPriceForm({ start_date: '', end_date: '', price: '' });
    } catch (err) {
      setError(err?.message || 'Failed to add price');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePrice = async (e) => {
    e.preventDefault();
    if (!editingPriceId) return;
    setError('');
    setSaving(true);
    try {
      const res = await api.put(`/owner/apartment-prices/${editingPriceId}`, {
        start_date: priceForm.start_date,
        end_date: priceForm.end_date,
        price: Number(priceForm.price),
      });
      setApartmentPrices((prev) => prev.map((p) => (p.id === res.data.id ? res.data : p)));
      setEditingPriceId(null);
      setPriceForm({ start_date: '', end_date: '', price: '' });
    } catch (err) {
      setError(err?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrice = async (price) => {
    const ok = await luxisttConfirm({
      title: 'Remove price range?',
      text: 'Delete this nightly rate window? Future quotes may change.',
      icon: 'warning',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/owner/apartment-prices/${price.id}`);
      setApartmentPrices((prev) => prev.filter((p) => p.id !== price.id));
    } catch (err) {
      setError(err?.message || 'Failed to delete');
    }
  };

  if (loading && !property) {
    return (
      <div className="dashboard-layout">
        <main className="dashboard-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="dashboard-loading">
            <div className="loading-spinner" />
            <p>Loading property…</p>
          </div>
        </main>
      </div>
    );
  }

  const filteredApartments = apartmentSearch.trim()
    ? apartments.filter(
        (a) =>
          (a.name || '').toLowerCase().includes(apartmentSearch.toLowerCase()) ||
          (a.apartment_type?.name || '').toLowerCase().includes(apartmentSearch.toLowerCase())
      )
    : apartments;

  let filteredExpenses = expenses;
  if (expenseCategoryFilter) {
    filteredExpenses = filteredExpenses.filter((ex) => ex.category === expenseCategoryFilter);
  }
  if (expenseDateFrom) {
    filteredExpenses = filteredExpenses.filter((ex) => (ex.expense_date || '').slice(0, 10) >= expenseDateFrom);
  }
  if (expenseDateTo) {
    filteredExpenses = filteredExpenses.filter((ex) => (ex.expense_date || '').slice(0, 10) <= expenseDateTo);
  }

  if (!property) {
    return (
      <div className="dashboard-layout">
        <main className="dashboard-main">
          <p>Property not found.</p>
          <Link to="/dashboard" className="back-link">← Back to dashboard</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <div className="sidebar-brand">
          <Link to="/">Luxistt</Link>
        </div>
        <div className="sidebar-nav">
          <Link to="/dashboard" className="sidebar-nav-item" style={{ textDecoration: 'none', color: 'inherit' }}>
            <FiArrowLeft className="sidebar-nav-icon" />
            <span>Back to dashboard</span>
          </Link>
        </div>
        <div className="sidebar-footer" />
      </aside>

      <main className="dashboard-main">
        <div className="dashboard-main-inner">
        <div className="property-detail-header">
          <div className="property-detail-header-left">
            <Link to="/dashboard" className="back-link"><FiArrowLeft /> Dashboard</Link>
            <h1>{property.name}</h1>
          </div>
          <div className="property-detail-header-tools">
            <NotificationBell />
            {canManage && (
              <button type="button" className="btn-ghost" onClick={() => setEditPropertyModal(true)}><FiEdit2 /> Edit</button>
            )}
          </div>
        </div>
        <p className="dashboard-subtitle">
          {property.city?.name} · {property.address_street} {property.address_postcode && `· ${property.address_postcode}`}
        </p>

        <div className="subsection subsection-table">
          <h2><FiHome /> Apartments</h2>
          {apartments.length === 0 ? (
            <p className="empty-state-text">No apartments yet. Add one to start receiving bookings.</p>
          ) : (
            <div className="table-card">
              <div className="table-toolbar">
                <div className="filter-group">
                  <label htmlFor="apt-search">
                    <FiSearch size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Search
                  </label>
                  <input
                    id="apt-search"
                    type="search"
                    placeholder="Name or type…"
                    value={apartmentSearch}
                    onChange={(e) => setApartmentSearch(e.target.value)}
                    aria-label="Search apartments"
                  />
                </div>
                <div className="toolbar-spacer" />
                {canManage && (
                <button type="button" className="btn-add" onClick={() => { setAddApartmentModal(true); setError(''); }}>
                  <FiPlus /> Add apartment
                </button>
              )}
              </div>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="col-num">Adults</th>
                      <th className="col-num">Children</th>
                      <th className="col-num">Bathrooms</th>
                      <th>Type</th>
                      <th className="col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApartments.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                          {apartmentSearch.trim() ? 'No apartments match your search.' : 'No apartments.'}
                        </td>
                      </tr>
                    ) : (
                      filteredApartments.map((a) => (
                        <tr key={a.id}>
                          <td><span className="cell-primary">{a.name}</span></td>
                          <td className="col-num">{a.capacity_adults}</td>
                          <td className="col-num">{a.capacity_children}</td>
                          <td className="col-num">{a.bathrooms}</td>
                          <td>{a.apartment_type?.name || '—'}</td>
                          <td className="col-actions">
                            <button type="button" className="btn-icon" onClick={() => openPricing(a)} title={canManage ? 'Set prices' : 'View prices'}><FiDollarSign /></button>
                            {canManage && (
                              <>
                                <button type="button" className="btn-icon" onClick={() => openEditApartment(a)} title="Edit"><FiEdit2 /></button>
                                <button type="button" className="btn-icon" onClick={() => handleDeleteApartment(a)} title="Delete"><FiTrash2 /></button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {apartments.length === 0 && canManage && (
            <button type="button" className="btn-add" style={{ marginTop: '0.75rem' }} onClick={() => { setAddApartmentModal(true); setError(''); }}>
              <FiPlus /> Add apartment
            </button>
          )}
        </div>

        <div className="subsection subsection-table">
          <h2><FiCreditCard /> Expenses</h2>
          {expenses.length === 0 ? (
            <p className="empty-state-text">No expenses recorded yet.</p>
          ) : (
            <div className="table-card">
              <div className="table-toolbar">
                <div className="filter-group">
                  <label htmlFor="exp-category">Category</label>
                  <select
                    id="exp-category"
                    value={expenseCategoryFilter}
                    onChange={(e) => setExpenseCategoryFilter(e.target.value)}
                    aria-label="Filter by category"
                  >
                    <option value="">All</option>
                    {EXPENSE_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="exp-from">From</label>
                  <input
                    id="exp-from"
                    type="date"
                    value={expenseDateFrom}
                    onChange={(e) => setExpenseDateFrom(e.target.value)}
                    aria-label="Filter from date"
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="exp-to">To</label>
                  <input
                    id="exp-to"
                    type="date"
                    value={expenseDateTo}
                    onChange={(e) => setExpenseDateTo(e.target.value)}
                    aria-label="Filter to date"
                  />
                </div>
                <div className="toolbar-spacer" />
                {canManageExpenses && (
                  <button type="button" className="btn-add" onClick={() => { setAddExpenseModal(true); setError(''); }}>
                    <FiPlus /> Add expense
                  </button>
                )}
              </div>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th className="col-amount">Amount</th>
                      <th>Apartment</th>
                      <th>Description</th>
                      <th className="col-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                          {expenseCategoryFilter || expenseDateFrom || expenseDateTo ? 'No expenses match filters.' : 'No expenses.'}
                        </td>
                      </tr>
                    ) : (
                      filteredExpenses.map((ex) => (
                        <tr key={ex.id}>
                          <td>{(ex.expense_date || '').slice(0, 10)}</td>
                          <td style={{ textTransform: 'capitalize' }}>{ex.category}</td>
                          <td className="col-amount">₦{Number(ex.amount).toLocaleString()}</td>
                          <td>{ex.apartment?.name || '—'}</td>
                          <td>{ex.description || '—'}</td>
                          <td className="col-actions">
                            {canManageExpenses && (
                              <>
                                <button type="button" className="btn-icon" onClick={() => openEditExpense(ex)} title="Edit"><FiEdit2 /></button>
                                <button type="button" className="btn-icon" onClick={() => handleDeleteExpense(ex)} title="Delete"><FiTrash2 /></button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {expenses.length === 0 && canManageExpenses && (
            <button type="button" className="btn-add" style={{ marginTop: '0.75rem' }} onClick={() => { setAddExpenseModal(true); setError(''); }}>
              <FiPlus /> Add expense
            </button>
          )}
        </div>
        <footer className="dashboard-footer">
          Powered by <a href="https://www.newistt.com" target="_blank" rel="noopener noreferrer">newistt</a>
        </footer>
        </div>
      </main>

      {editPropertyModal && (
        <div className="modal-backdrop" onClick={() => setEditPropertyModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Edit property</h3>
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleUpdateProperty}>
              <label>Name</label>
              <input type="text" value={propertyForm.name} onChange={(e) => setPropertyForm((f) => ({ ...f, name: e.target.value }))} required />
              <label>City</label>
              <select value={propertyForm.city_id} onChange={(e) => setPropertyForm((f) => ({ ...f, city_id: e.target.value }))} required>
                <option value="">Select city</option>
                {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <label>Street address</label>
              <input type="text" value={propertyForm.address_street} onChange={(e) => setPropertyForm((f) => ({ ...f, address_street: e.target.value }))} required />
              <label>Postcode</label>
              <input type="text" value={propertyForm.address_postcode} onChange={(e) => setPropertyForm((f) => ({ ...f, address_postcode: e.target.value }))} />
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setEditPropertyModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addApartmentModal && (
        <div className="modal-backdrop" onClick={() => setAddApartmentModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Add apartment</h3>
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleAddApartment}>
              <label>Name</label>
              <input type="text" value={apartmentForm.name} onChange={(e) => setApartmentForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Unit 1" required />
              <label>Adults capacity</label>
              <input type="number" min="1" value={apartmentForm.capacity_adults} onChange={(e) => setApartmentForm((f) => ({ ...f, capacity_adults: e.target.value }))} />
              <label>Children capacity</label>
              <input type="number" min="0" value={apartmentForm.capacity_children} onChange={(e) => setApartmentForm((f) => ({ ...f, capacity_children: e.target.value }))} />
              <label>Bathrooms</label>
              <input type="number" min="0" value={apartmentForm.bathrooms} onChange={(e) => setApartmentForm((f) => ({ ...f, bathrooms: e.target.value }))} />
              <label>Type (optional)</label>
              <select value={apartmentForm.apartment_type_id} onChange={(e) => setApartmentForm((f) => ({ ...f, apartment_type_id: e.target.value }))}>
                <option value="">—</option>
                {apartmentTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <label>Size m² (optional)</label>
              <input type="number" min="0" value={apartmentForm.size} onChange={(e) => setApartmentForm((f) => ({ ...f, size: e.target.value }))} placeholder="e.g. 65" />
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setAddApartmentModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add apartment'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editApartmentModal && (
        <div className="modal-backdrop" onClick={() => setEditApartmentModal(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Edit apartment</h3>
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleUpdateApartment}>
              <label>Name</label>
              <input type="text" value={apartmentForm.name} onChange={(e) => setApartmentForm((f) => ({ ...f, name: e.target.value }))} required />
              <label>Adults capacity</label>
              <input type="number" min="1" value={apartmentForm.capacity_adults} onChange={(e) => setApartmentForm((f) => ({ ...f, capacity_adults: e.target.value }))} />
              <label>Children capacity</label>
              <input type="number" min="0" value={apartmentForm.capacity_children} onChange={(e) => setApartmentForm((f) => ({ ...f, capacity_children: e.target.value }))} />
              <label>Bathrooms</label>
              <input type="number" min="0" value={apartmentForm.bathrooms} onChange={(e) => setApartmentForm((f) => ({ ...f, bathrooms: e.target.value }))} />
              <label>Type (optional)</label>
              <select value={apartmentForm.apartment_type_id} onChange={(e) => setApartmentForm((f) => ({ ...f, apartment_type_id: e.target.value }))}>
                <option value="">—</option>
                {apartmentTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <label>Size m² (optional)</label>
              <input type="number" min="0" value={apartmentForm.size} onChange={(e) => setApartmentForm((f) => ({ ...f, size: e.target.value }))} />
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setEditApartmentModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {addExpenseModal && canManageExpenses && (
        <div className="modal-backdrop" onClick={() => setAddExpenseModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Add expense</h3>
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleAddExpense}>
              <label>Amount (₦)</label>
              <input type="number" min="0" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm((f) => ({ ...f, amount: e.target.value }))} required />
              <label>Category</label>
              <select value={expenseForm.category} onChange={(e) => setExpenseForm((f) => ({ ...f, category: e.target.value }))}>
                {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <label>Date</label>
              <input type="date" value={expenseForm.expense_date} onChange={(e) => setExpenseForm((f) => ({ ...f, expense_date: e.target.value }))} required />
              <label>Apartment (optional)</label>
              <select value={expenseForm.apartment_id} onChange={(e) => setExpenseForm((f) => ({ ...f, apartment_id: e.target.value }))}>
                <option value="">—</option>
                {apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <label>Description (optional)</label>
              <textarea value={expenseForm.description} onChange={(e) => setExpenseForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setAddExpenseModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add expense'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingExpense && canManageExpenses && (
        <div className="modal-backdrop" onClick={() => setEditingExpense(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>Edit expense</h3>
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            <form onSubmit={handleUpdateExpense}>
              <label>Amount (₦)</label>
              <input type="number" min="0" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm((f) => ({ ...f, amount: e.target.value }))} required />
              <label>Category</label>
              <select value={expenseForm.category} onChange={(e) => setExpenseForm((f) => ({ ...f, category: e.target.value }))}>
                {EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <label>Date</label>
              <input type="date" value={expenseForm.expense_date} onChange={(e) => setExpenseForm((f) => ({ ...f, expense_date: e.target.value }))} required />
              <label>Apartment (optional)</label>
              <select value={expenseForm.apartment_id} onChange={(e) => setExpenseForm((f) => ({ ...f, apartment_id: e.target.value }))}>
                <option value="">—</option>
                {apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <label>Description (optional)</label>
              <textarea value={expenseForm.description} onChange={(e) => setExpenseForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setEditingExpense(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pricingApartment && (
        <div className="modal-backdrop" onClick={() => setPricingApartment(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <h3><FiDollarSign /> Prices – {pricingApartment.name}</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Set price per night for date ranges. Bookings use these to calculate total.</p>
            {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
            {apartmentPrices.length > 0 && (
              <div className="table-wrap" style={{ marginBottom: '1rem' }}>
                <table className="data-table">
                  <thead><tr><th>From</th><th>To</th><th className="col-amount">Price/night (₦)</th><th className="col-actions"></th></tr></thead>
                  <tbody>
                    {apartmentPrices.map((p) => (
                      <tr key={p.id}>
                        <td>{(p.start_date || '').slice(0, 10)}</td>
                        <td>{(p.end_date || '').slice(0, 10)}</td>
                        <td className="col-amount">₦{Number(p.price).toLocaleString()}</td>
                        <td className="col-actions">
                          {canManage && editingPriceId === p.id ? null : canManage && editingPriceId !== p.id ? (
                            <>
                              <button type="button" className="btn-icon" onClick={() => { setEditingPriceId(p.id); setPriceForm({ start_date: (p.start_date || '').slice(0, 10), end_date: (p.end_date || '').slice(0, 10), price: String(p.price) }); }} title="Edit"><FiEdit2 /></button>
                              <button type="button" className="btn-icon" onClick={() => handleDeletePrice(p)} title="Delete"><FiTrash2 /></button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {canManage && editingPriceId ? (
              <form onSubmit={handleUpdatePrice}>
                <label>From</label>
                <input type="date" value={priceForm.start_date} onChange={(e) => setPriceForm((f) => ({ ...f, start_date: e.target.value }))} required />
                <label>To</label>
                <input type="date" value={priceForm.end_date} onChange={(e) => setPriceForm((f) => ({ ...f, end_date: e.target.value }))} required />
                <label>Price per night (₦)</label>
                <input type="number" min={0} value={priceForm.price} onChange={(e) => setPriceForm((f) => ({ ...f, price: e.target.value }))} required />
                <div className="modal-actions">
                  <button type="button" className="btn-ghost" onClick={() => { setEditingPriceId(null); setPriceForm({ start_date: '', end_date: '', price: '' }); }}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={saving}>Save</button>
                </div>
              </form>
            ) : canManage && !editingPriceId ? (
              <form onSubmit={handleAddPrice}>
                <label>From</label>
                <input type="date" value={priceForm.start_date} onChange={(e) => setPriceForm((f) => ({ ...f, start_date: e.target.value }))} required />
                <label>To</label>
                <input type="date" value={priceForm.end_date} onChange={(e) => setPriceForm((f) => ({ ...f, end_date: e.target.value }))} required />
                <label>Price per night (₦)</label>
                <input type="number" min={0} value={priceForm.price} onChange={(e) => setPriceForm((f) => ({ ...f, price: e.target.value }))} placeholder="e.g. 25000" required />
                <div className="modal-actions">
                  <button type="button" className="btn-ghost" onClick={() => setPricingApartment(null)}>Close</button>
                  <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Adding…' : 'Add price range'}</button>
                </div>
              </form>
            ) : (
              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setPricingApartment(null)}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
